import React, { useState, useEffect, useCallback } from "react";
import WorkoutTracker from "./index";
import { peopleAPI } from "../../services/peopleAPI";
import { setActivePerson } from "./services/workoutAPI";
import "./styles/WorkoutTracker.scss";

// Per-device: each device remembers who it was last used for, so a shared
// account on two devices does not fight over one setting. The stored id is
// always validated against the fetched list, so a value left by another
// account simply falls through to that account's default person.
//
// Deliberately a different key from the macro calculator's: the two features
// are used at different times (kitchen vs gym), and forcing one selection on
// both would make switching for a workout silently retarget macro logging.
const ACTIVE_PERSON_KEY = "workoutTracker.activePersonId";

const WorkoutTrackerPage = () => {
  const [people, setPeople] = useState([]);
  const [activePersonId, setActivePersonId] = useState(null);
  const [peopleLoaded, setPeopleLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Push the selection into the API module before anything renders against it.
  // Done here rather than in an effect because child effects run before parent
  // effects in React - by the time a parent effect fired, WorkoutTracker's
  // mount effects would already have loaded the wrong person's data.
  const applyPerson = useCallback((id) => {
    setActivePerson(id);
    setActivePersonId(id);
  }, []);

  const loadPeople = useCallback(
    async (preferredId = null) => {
      const { people: list, defaultPersonId } = await peopleAPI.list();
      setPeople(list);

      const stored =
        preferredId ?? parseInt(localStorage.getItem(ACTIVE_PERSON_KEY), 10);
      const valid = list.some((p) => p.id === stored);
      const next = valid ? stored : defaultPersonId ?? list[0]?.id ?? null;

      if (next != null) localStorage.setItem(ACTIVE_PERSON_KEY, String(next));
      applyPerson(next);
      return list;
    },
    [applyPerson]
  );

  useEffect(() => {
    (async () => {
      try {
        await loadPeople();
      } catch (error) {
        console.error("Error loading people:", error);
        setLoadError("Could not load people for this account.");
      } finally {
        setPeopleLoaded(true);
      }
    })();
  }, [loadPeople]);

  const selectPerson = (id) => {
    if (id === activePersonId) return;
    localStorage.setItem(ACTIVE_PERSON_KEY, String(id));
    applyPerson(id);
  };

  // Gate the tracker until the active person is known. Mounting it with no
  // person would let its mount effects load the DEFAULT person's draft and
  // active programs, which the autosave could then write back under whoever
  // was actually selected a moment later.
  if (!peopleLoaded) {
    return (
      <div className="workout-tracker-container">
        <p className="text-center mt-4">Loading…</p>
      </div>
    );
  }

  if (loadError || activePersonId == null) {
    return (
      <div className="workout-tracker-container">
        <p className="text-center mt-4">
          {loadError || "No person available."}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Person Switcher - logs, drafts, PRs and program progress follow this
          selection. Exercises, templates and programs are shared. People are
          added and removed from the macro calculator, which owns that UI. */}
      {people.length > 0 && (
        <div className="person-switcher workout-person-switcher">
          {people.map((person) => (
            <button
              key={person.id}
              className={`person-pill ${
                person.id === activePersonId ? "active" : ""
              }`}
              onClick={() => selectPerson(person.id)}
              title={`Train as ${person.name}`}
            >
              {person.icon ? `${person.icon} ` : ""}
              {person.name}
            </button>
          ))}
        </div>
      )}

      <WorkoutTracker
        // Remounting on switch resets all of the tracker's local state at once
        // - exhaustive by construction, unlike a hand-written reset. It also
        // gives useWorkoutDraft's unmount cleanup a chance to flush the
        // outgoing person's draft (stamped with the person it was typed under).
        key={activePersonId}
      />
    </>
  );
};

export default WorkoutTrackerPage;
